import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Button, Box } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  enablePush, getPushStatus, isIos, isStandalone, PUSH_CHANGED_EVENT,
} from '../../services/push';

/**
 * Como se arreglan los permisos segun donde este corriendo la app. En la app de
 * Android (el APK) las notificaciones son las de la propia app CNPK GPI, no las
 * de Chrome: el APK delega en ella.
 */
export function blockedHelp() {
  if (isIos()) return 'Ajustes del iPhone > Notificaciones > CNPK GPI > Permitir notificaciones.';
  if (isStandalone()) return 'Ajustes del telefono > Aplicaciones > CNPK GPI > Notificaciones > Permitir.';
  return 'Pulsa el candado junto a la direccion y pon Notificaciones en "Permitir".';
}

/**
 * Aviso para activar las notificaciones. El permiso solo puede pedirse desde
 * un boton, y si se deniega ya no se puede volver a preguntar desde la app:
 * hay que ir a los ajustes del telefono, y eso es lo que se explica.
 *
 * Decide por el estado real (`getPushStatus`: hay suscripcion y permiso), no solo
 * por Notification.permission, que en la app de Android puede no reflejar lo que
 * el sistema ya ha concedido. Se recalcula al volver a la app, porque quien
 * activa algo en Ajustes vuelve a ella.
 */
export default function PushBanner() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(() => getPushStatus().then(setStatus).catch(() => {}), []);

  useEffect(() => {
    refresh();
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(PUSH_CHANGED_EVENT, refresh);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(PUSH_CHANGED_EVENT, refresh);
    };
  }, [refresh]);

  if (!status || status.state === 'active' || status.state === 'unsupported') return null;

  // iPhone: en una pestana de Safari no hay notificaciones; hay que instalar la
  // app en la pantalla de inicio y abrirla desde alli
  if (status.state === 'ios-install') {
    return (
      <Alert severity="info" icon={<NotificationsActiveIcon />} sx={{ m: 1 }}>
        Para recibir notificaciones en el iPhone, instala la app: pulsa{' '}
        <strong>Compartir</strong> (el cuadrado con la flecha) y despues{' '}
        <strong>Anadir a pantalla de inicio</strong>. Abrela desde ese icono.
      </Alert>
    );
  }

  if (status.state === 'denied') {
    return (
      <Alert severity="warning" sx={{ m: 1 }}>
        Las notificaciones estan bloqueadas, y asi no llegaran los mensajes con la app
        cerrada. Activalas en: {blockedHelp()}
      </Alert>
    );
  }

  const activate = async () => {
    setBusy(true);
    setError('');
    const result = await enablePush();
    if (!result.ok) {
      setError(`${result.error}. Abre "Probar notificaciones" en el menu de la cuenta para ver los detalles.`);
    }
    await refresh();
    setBusy(false);
  };

  return (
    <Alert severity="info" icon={<NotificationsActiveIcon />} sx={{ m: 1 }}
      action={<Button color="inherit" size="small" onClick={activate} disabled={busy}>Activar</Button>}>
      Activa las notificaciones para enterarte de los mensajes aunque no tengas la app abierta.
      {error && <Box sx={{ color: 'error.main', mt: 0.5, wordBreak: 'break-word' }}>{error}</Box>}
    </Alert>
  );
}
